import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useTimer } from '../../context/TimerContext';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  aiAssistantAPI,
  aiConversationsAPI,
  AiMessage,
  projectsAPI,
  VoiceActionResult,
} from '../../services/api';
import {
  SparklesIcon,
  FolderRoundedIcon,
  ChevronDownPathIcon,
  InfoIcon,
  CircleIcon,
  MessageIcon,
  SendPlaneIcon,
  MicOffIcon,
} from '../icons';

// Icons
const Icons = {
  sparkles: <SparklesIcon className="w-6 h-6" strokeWidth={2} />,
  folder: <FolderRoundedIcon className="w-4 h-4" strokeWidth={2} />,
  chevronDown: <ChevronDownPathIcon className="w-4 h-4" strokeWidth={2} />,
  info: <InfoIcon className="w-4 h-4" strokeWidth={2} />,
  circle: <CircleIcon className="w-4 h-4" strokeWidth={2} />,
  message: <MessageIcon className="w-8 h-8" strokeWidth={1.5} />,
  send: <SendPlaneIcon className="w-5 h-5" strokeWidth={2} />,
  // Mic and spinner use mixed fill/opacity/animation that is unique to this
  // file; kept inline to avoid altering rendered output.
  mic: (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
      <rect x="9" y="1" width="6" height="11" rx="3" />
      <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
      <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  micOff: <MicOffIcon className="w-5 h-5" strokeWidth={2} />,
  spinner: (
    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  ),
};

interface Project {
  id: number;
  name: string;
  client?: { name: string } | null;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

type VoiceListenState = 'idle' | 'listening' | 'processing';

interface AiChatProps {
  conversationId?: number;
  initialProjectId?: number;
  onConversationChange?: (conversationId: number) => void;
  contextType?: 'general' | 'time_entry_help' | 'time_entry_analysis';
  timeEntryId?: number;
  onVoiceInput?: (transcript: string) => void;
}

const AiChat: React.FC<AiChatProps> = ({
  conversationId: initialConversationId,
  initialProjectId,
  onConversationChange,
  contextType = 'general',
  timeEntryId,
  onVoiceInput,
}) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const { startTimer, stopTimer } = useTimer();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<AiMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const streamingContentRef = useRef('');
  const [conversationId, setConversationId] = useState<number | undefined>(
    initialConversationId,
  );
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Project context
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<
    number | undefined
  >(initialProjectId);
  const [currentProject, setCurrentProject] = useState<{
    id: number;
    name: string;
    client_name?: string;
  } | null>(null);
  const [hasIntegrationData, setHasIntegrationData] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(false);

  // Voice input state
  const [voiceState, setVoiceState] = useState<VoiceListenState>('idle');
  const voiceStateRef = useRef<VoiceListenState>('idle');
  const recognitionRef = useRef<any>(null);

  const isVoiceSupported =
    typeof window !== 'undefined' &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition);

  // Load projects on mount
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const response = await projectsAPI.getAll();
        setProjects(response.data.projects || []);
      } catch (err) {
        console.error('Failed to load projects', err);
      }
    };
    loadProjects();
  }, []);

  useEffect(() => {
    if (initialConversationId) {
      loadConversation(initialConversationId);
    }
  }, [initialConversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  const loadConversation = async (id: number) => {
    try {
      setIsLoading(true);
      const response = await aiConversationsAPI.getById(id);
      const conv = response.data.conversation;
      setMessages(conv.messages || []);
      setConversationId(id);
      // Load project context from conversation
      if (conv.project) {
        setCurrentProject(conv.project);
        setSelectedProjectId(conv.project.id);
        setHasIntegrationData(conv.has_integration_data || false);
      } else {
        setCurrentProject(null);
        setSelectedProjectId(undefined);
        setHasIntegrationData(false);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleProjectChange = async (projectId: number | undefined) => {
    setSelectedProjectId(projectId);
    setShowProjectSelector(false);

    if (projectId) {
      const project = projects.find((p) => p.id === projectId);
      if (project) {
        setCurrentProject({
          id: project.id,
          name: project.name,
          client_name: project.client?.name,
        });
      }
    } else {
      setCurrentProject(null);
    }

    // Update existing conversation's project if we have one
    if (conversationId) {
      try {
        await aiConversationsAPI.update(conversationId, {
          project_id: projectId || null,
        });
        // Reload to get has_integration_data
        const response = await aiConversationsAPI.getById(conversationId);
        setHasIntegrationData(
          response.data.conversation.has_integration_data || false,
        );
      } catch (err) {
        console.error('Failed to update conversation project', err);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const executeAction = useCallback((actionResult?: VoiceActionResult) => {
    if (!actionResult?.action) return;

    switch (actionResult.action) {
      case 'start_timer':
        if (actionResult.project_id) {
          startTimer(actionResult.project_id);
        }
        break;
      case 'stop_timer':
        stopTimer();
        break;
      case 'navigate':
        if (actionResult.destination) {
          navigate(`/${actionResult.destination}`);
        }
        break;
      default:
        setError(`This action isn't supported yet.`);
        break;
    }
  }, [startTimer, stopTimer, navigate]);

  const sendMessage = useCallback(async (userMessage: string) => {
    if (!userMessage.trim() || isLoading || isStreaming) return;

    setError(null);

    // Add user message to UI immediately
    const tempUserMessage: AiMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMessage]);

    setIsStreaming(true);
    setStreamingContent('');
    streamingContentRef.current = '';

    try {
      await aiAssistantAPI.streamChat(
        {
          message: userMessage,
          conversation_id: conversationId,
          context_type: contextType,
          project_id: selectedProjectId,
        },
        // onChunk
        (chunk) => {
          streamingContentRef.current += chunk;
          setStreamingContent((prev) => prev + chunk);
        },
        // onDone
        (newConversationId, actionResult) => {
          // Add assistant message
          const assistantMessage: AiMessage = {
            id: Date.now() + 1,
            role: 'assistant',
            content: streamingContentRef.current,
            created_at: new Date().toISOString(),
          };

          setMessages((prev) => [...prev, assistantMessage]);

          streamingContentRef.current = '';
          setStreamingContent('');
          setIsStreaming(false);

          if (newConversationId && newConversationId !== conversationId) {
            setConversationId(newConversationId);
            onConversationChange?.(newConversationId);
          }

          // Execute voice action if present
          if (actionResult) {
            executeAction(actionResult);
          }
        },
        // onError
        (errorMsg) => {
          setError(errorMsg);
          setIsStreaming(false);
          streamingContentRef.current = '';
          setStreamingContent('');
        },
      );
    } catch (err: any) {
      setError(err.message || 'Failed to send message');
      setIsStreaming(false);
    }
  }, [isLoading, isStreaming, conversationId, contextType, selectedProjectId, onConversationChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    const msg = input.trim();
    setInput('');
    sendMessage(msg);
  };

  // Voice input logic
  const setVoiceListenState = (s: VoiceListenState) => {
    voiceStateRef.current = s;
    setVoiceState(s);
  };

  const startVoiceListening = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognitionRef.current = recognition;

    recognition.continuous = false;
    recognition.interimResults = false;
    const lng = localStorage.getItem('i18nextLng') || 'fr-CA';
    recognition.lang = lng.startsWith('fr') ? 'fr-CA' : 'en-US';
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setVoiceListenState('listening');

    recognition.onresult = (event: any) => {
      const result = event.results[event.results.length - 1];
      const text = result[0].transcript;
      if (result.isFinal) {
        setVoiceListenState('idle');
        onVoiceInput?.(text);
        setInput(text);
        sendMessage(text);
      }
    };

    recognition.onerror = (event: any) => {
      setVoiceListenState('idle');
      if (event.error === 'not-allowed') {
        setError('Microphone access was denied. Please allow microphone permission and try again.');
      } else if (event.error !== 'aborted') {
        setError('Voice recognition error. Please try again.');
      }
    };

    recognition.onend = () => {
      if (voiceStateRef.current === 'listening') setVoiceListenState('idle');
    };

    recognition.start();
  }, [sendMessage, onVoiceInput]);

  const handleVoiceClick = () => {
    if (voiceStateRef.current === 'listening') {
      recognitionRef.current?.stop();
      setVoiceListenState('idle');
    } else if (voiceStateRef.current === 'idle') {
      startVoiceListening();
    }
  };

  useEffect(() => () => recognitionRef.current?.stop(), []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const MarkdownContent: React.FC<{ content: string; isUser?: boolean }> = ({
    content,
    isUser,
  }) => (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // Headings
        h1: ({ children }) => (
          <h1 className="text-xl font-bold mb-2 mt-3">{children}</h1>
        ),
        h2: ({ children }) => (
          <h2 className="text-lg font-bold mb-2 mt-3">{children}</h2>
        ),
        h3: ({ children }) => (
          <h3 className="text-base font-bold mb-1 mt-2">{children}</h3>
        ),
        // Paragraphs
        p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
        // Lists
        ul: ({ children }) => (
          <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal list-inside mb-2 space-y-1">
            {children}
          </ol>
        ),
        li: ({ children }) => <li className="ml-2">{children}</li>,
        // Code
        code: ({ className, children, ...props }) => {
          const isInline = !className;
          if (isInline) {
            return (
              <code
                className={`px-1.5 py-0.5 rounded text-sm font-mono ${
                  isUser
                    ? 'bg-white/20'
                    : isDark
                      ? 'bg-slate-700 text-violet-300'
                      : 'bg-slate-200 text-slate-800'
                }`}
                {...props}
              >
                {children}
              </code>
            );
          }
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        },
        pre: ({ children }) => (
          <pre
            className={`p-3 rounded-xl overflow-x-auto text-sm font-mono mb-2 ${
              isUser
                ? 'bg-white/20'
                : isDark
                  ? 'bg-slate-900 text-slate-100'
                  : 'bg-slate-900 text-slate-100'
            }`}
          >
            {children}
          </pre>
        ),
        // Links
        a: ({ href, children }) => (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={`underline ${
              isUser
                ? 'text-violet-200 hover:text-white'
                : isDark
                  ? 'text-violet-400 hover:text-violet-300'
                  : 'text-violet-600 hover:text-violet-800'
            }`}
          >
            {children}
          </a>
        ),
        // Blockquote
        blockquote: ({ children }) => (
          <blockquote
            className={`border-l-4 pl-3 italic my-2 ${
              isUser
                ? 'border-white/40 text-violet-100'
                : isDark
                  ? 'border-violet-500/50 text-slate-400'
                  : 'border-violet-300 text-slate-600'
            }`}
          >
            {children}
          </blockquote>
        ),
        // Tables
        table: ({ children }) => (
          <div className="overflow-x-auto mb-2">
            <table className="min-w-full border-collapse text-sm">
              {children}
            </table>
          </div>
        ),
        th: ({ children }) => (
          <th
            className={`border px-2 py-1 text-left font-semibold ${
              isUser
                ? 'border-white/30 bg-white/10'
                : isDark
                  ? 'border-slate-600 bg-slate-700'
                  : 'border-slate-300 bg-slate-100'
            }`}
          >
            {children}
          </th>
        ),
        td: ({ children }) => (
          <td
            className={`border px-2 py-1 ${
              isUser
                ? 'border-white/30'
                : isDark
                  ? 'border-slate-600'
                  : 'border-slate-300'
            }`}
          >
            {children}
          </td>
        ),
        // Strong and emphasis
        strong: ({ children }) => (
          <strong className="font-semibold">{children}</strong>
        ),
        em: ({ children }) => <em className="italic">{children}</em>,
        // Horizontal rule
        hr: () => (
          <hr
            className={`my-3 ${
              isUser
                ? 'border-white/30'
                : isDark
                  ? 'border-slate-600'
                  : 'border-slate-300'
            }`}
          />
        ),
      }}
    >
      {content}
    </ReactMarkdown>
  );

  return (
    <div
      className={`flex flex-col h-full rounded-2xl border overflow-hidden ${
        isDark
          ? 'bg-slate-900/50 border-slate-700/50'
          : 'bg-white border-slate-200'
      }`}
    >
      {/* Header */}
      <div
        className="px-4 sm:px-6 py-4"
        style={{
          background:
            'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4c1d95 100%)',
        }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex w-10 h-10 bg-white/20 rounded-xl items-center justify-center text-white">
              {Icons.sparkles}
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white">
                {t('ai.assistant.title')}
              </h2>
              <p className="text-xs sm:text-sm text-violet-200">
                {t('ai.assistant.subtitle')}
              </p>
            </div>
          </div>

          {/* Project Context Selector */}
          <div className="relative">
            <button
              onClick={() => setShowProjectSelector(!showProjectSelector)}
              className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-xl text-white text-sm transition-all duration-200"
            >
              <div className="flex items-center gap-2">
                {Icons.folder}
                <span className="max-w-[200px] sm:max-w-[150px] truncate">
                  {currentProject
                    ? currentProject.name
                    : t('ai.assistant.noProjectContext')}
                </span>
                {hasIntegrationData && (
                  <span
                    className="w-2 h-2 bg-emerald-400 rounded-full"
                    title={t('ai.assistant.hasIntegrationData')}
                  />
                )}
              </div>
              {Icons.chevronDown}
            </button>

            {showProjectSelector && (
              <div
                className={`absolute right-0 top-full mt-2 w-72 rounded-xl shadow-xl border z-50 max-h-80 overflow-y-auto ${
                  isDark
                    ? 'bg-slate-900 border-slate-700'
                    : 'bg-white border-slate-200'
                }`}
              >
                <div
                  className={`p-2 border-b ${isDark ? 'border-slate-700' : 'border-slate-100'}`}
                >
                  <div
                    className={`text-xs font-medium uppercase px-2 py-1 ${
                      isDark ? 'text-slate-500' : 'text-slate-500'
                    }`}
                  >
                    {t('ai.assistant.selectProjectContext')}
                  </div>
                </div>
                <div className="p-1">
                  {/* No project option */}
                  <button
                    onClick={() => handleProjectChange(undefined)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all duration-200 ${
                      !selectedProjectId
                        ? isDark
                          ? 'bg-violet-500/20 text-violet-300'
                          : 'bg-violet-50 text-violet-700'
                        : isDark
                          ? 'text-slate-300 hover:bg-slate-700'
                          : 'text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    <span
                      className={isDark ? 'text-slate-500' : 'text-slate-400'}
                    >
                      {Icons.circle}
                    </span>
                    {t('ai.assistant.generalContext')}
                  </button>

                  {/* Projects list */}
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleProjectChange(project.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-all duration-200 ${
                        selectedProjectId === project.id
                          ? isDark
                            ? 'bg-violet-500/20 text-violet-300'
                            : 'bg-violet-50 text-violet-700'
                          : isDark
                            ? 'text-slate-300 hover:bg-slate-700'
                            : 'text-slate-700 hover:bg-slate-100'
                      }`}
                    >
                      <span
                        className={isDark ? 'text-slate-500' : 'text-slate-400'}
                      >
                        {Icons.folder}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="truncate font-medium">
                          {project.name}
                        </div>
                        {project.client?.name && (
                          <div
                            className={`text-xs truncate ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                          >
                            {project.client.name}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project context indicator */}
        {currentProject && (
          <div className="mt-3 flex items-center gap-2 text-sm text-violet-200">
            {Icons.info}
            <span>
              {t('ai.assistant.usingProjectContext')}:{' '}
              <strong className="text-white">{currentProject.name}</strong>
              {currentProject.client_name && ` (${currentProject.client_name})`}
            </span>
            {hasIntegrationData && (
              <span className="px-2 py-0.5 bg-emerald-500/30 text-emerald-200 rounded-full text-xs">
                {t('ai.assistant.integrationsActive')}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Messages */}
      <div
        className={`flex-1 overflow-y-auto p-6 space-y-4 ${
          isDark ? 'bg-slate-900/50' : 'bg-slate-50'
        }`}
      >
        {messages.length === 0 && !isStreaming && (
          <div className="text-center py-12">
            <div
              className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                isDark
                  ? 'bg-gradient-to-br from-violet-900 to-purple-900 text-violet-400'
                  : 'bg-gradient-to-br from-violet-100 to-purple-100 text-violet-600'
              }`}
            >
              {Icons.message}
            </div>
            <h3
              className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('ai.assistant.welcome')}
            </h3>
            <p
              className={`max-w-md mx-auto ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
            >
              {t('ai.assistant.welcomeMessage')}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-2">
              {[
                t('ai.assistant.suggestions.sred'),
                t('ai.assistant.suggestions.cdae'),
                t('ai.assistant.suggestions.timeEntry'),
              ].map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setInput('');
                    sendMessage(suggestion);
                  }}
                  className={`px-4 py-2 text-sm rounded-xl transition-all duration-200 ${
                    isDark
                      ? 'bg-slate-900 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700'
                      : 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 hover:border-violet-300'
                  }`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${
              message.role === 'user' ? 'justify-end' : 'justify-start'
            }`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-gradient-to-r from-violet-600 to-purple-600 text-white'
                  : isDark
                    ? 'bg-slate-900 text-slate-200 border border-slate-700'
                    : 'bg-white text-slate-800 border border-slate-200'
              }`}
            >
              <div className="text-sm prose prose-sm max-w-none">
                <MarkdownContent
                  content={message.content}
                  isUser={message.role === 'user'}
                />
              </div>
              <div
                className={`text-xs mt-2 ${
                  message.role === 'user'
                    ? 'text-violet-200'
                    : isDark
                      ? 'text-slate-500'
                      : 'text-slate-400'
                }`}
              >
                {new Date(message.created_at).toLocaleTimeString()}
              </div>
            </div>
          </div>
        ))}

        {/* Streaming message */}
        {isStreaming && streamingContent && (
          <div className="flex justify-start">
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                isDark
                  ? 'bg-slate-900 text-slate-200 border border-slate-700'
                  : 'bg-white text-slate-800 border border-slate-200'
              }`}
            >
              <div
                className="text-sm prose prose-sm max-w-none"
                data-streaming-content
              >
                <MarkdownContent content={streamingContent} />
              </div>
              <div className="flex items-center mt-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-violet-600 rounded-full animate-bounce" />
                  <div
                    className="w-2 h-2 bg-violet-600 rounded-full animate-bounce"
                    style={{ animationDelay: '0.1s' }}
                  />
                  <div
                    className="w-2 h-2 bg-violet-600 rounded-full animate-bounce"
                    style={{ animationDelay: '0.2s' }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <div className="flex justify-start">
            <div
              className={`rounded-2xl px-4 py-3 ${
                isDark
                  ? 'bg-slate-900 border border-slate-700'
                  : 'bg-white border border-slate-200'
              }`}
            >
              <div className="flex gap-1">
                <div
                  className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`}
                />
                <div
                  className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`}
                  style={{ animationDelay: '0.1s' }}
                />
                <div
                  className={`w-2 h-2 rounded-full animate-bounce ${isDark ? 'bg-slate-500' : 'bg-slate-400'}`}
                  style={{ animationDelay: '0.2s' }}
                />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            className={`rounded-xl p-4 text-sm ${
              isDark
                ? 'bg-rose-500/10 border border-rose-500/20 text-rose-400'
                : 'bg-rose-50 border border-rose-200 text-rose-700'
            }`}
          >
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div
        className={`p-4 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}
      >
        <form
          onSubmit={handleSubmit}
          className="flex gap-3 flex-col md:flex-row"
        >
          <div className="flex-1  relative">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('ai.assistant.placeholder')}
              className={`w-full px-4 py-3 rounded-xl resize-none transition-all duration-200 ${
                isDark
                  ? 'bg-slate-900 border-slate-700 text-white placeholder-slate-500 focus:border-violet-500 focus:ring-violet-500/20'
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400 focus:border-violet-500 focus:ring-violet-500/20'
              } border focus:ring-2 focus:outline-none`}
              rows={1}
              disabled={isLoading || isStreaming}
            />
          </div>
          {isVoiceSupported && (
            <button
              type="button"
              onClick={handleVoiceClick}
              disabled={isLoading || isStreaming || voiceState === 'processing'}
              title={voiceState === 'listening' ? t('ai.assistant.voiceStop') : t('ai.assistant.voiceStart')}
              className={`p-3 rounded-xl shadow-lg transition-all duration-200 flex items-center justify-center disabled:cursor-not-allowed ${
                voiceState === 'listening'
                  ? 'bg-red-500 hover:bg-red-600 text-white shadow-red-500/25 animate-pulse'
                  : voiceState === 'processing'
                  ? 'bg-indigo-500 text-white shadow-indigo-500/25'
                  : isDark
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200'
              }`}
            >
              {voiceState === 'processing' ? Icons.spinner : voiceState === 'listening' ? Icons.micOff : Icons.mic}
            </button>
          )}
          <button
            type="submit"
            disabled={!input.trim() || isLoading || isStreaming}
            className="px-5 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-xl shadow-lg shadow-violet-500/25 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-between gap-2 font-semibold"
          >
            <span>{t('ai.assistant.send')}</span>
            {Icons.send}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AiChat;
